/**
 * StationOverview — shared component that renders a station-based production
 * progress view.  Self-contained: fetches station data, listens to WebSocket
 * updates, runs a 60-second tick timer, and respects the "hide disabled
 * stations" localStorage toggle.
 *
 * Used by SupervisorDashboard and Manager Dashboard (behind a toggle).
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import stationManagementService, { StationWithDetails } from '../services/stationManagementService';
import { useLanguage } from '../i18n/LanguageContext';
import SessionDetailsModal, { getDefaultSessionTab } from '../pages/Supervisor/components/SessionDetailsModal';
import { useAuth } from '../hooks/useAuth';
import { wsService } from '../services/websocket';
import { Scissors, PackageCheck } from 'lucide-react';
import { getStatusBadgeClass } from '../utils/statusColors';
import { QUERY_REFETCH_NORMAL, TICK_INTERVAL } from '../constants/queryConfig';

const StationOverview: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Station detail modal state
  const [selectedStation, setSelectedStation] = useState<StationWithDetails | null>(null);
  const [isSessionDetailsModalOpen, setIsSessionDetailsModalOpen] = useState(false);

  // Hide disabled stations toggle — synced with Station Management via localStorage
  const [hideDisabledStations, setHideDisabledStations] = useState(() =>
    localStorage.getItem('hideDisabledStations') === 'true'
  );

  // Tick state — increments every 60s so elapsed-time labels re-render
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), TICK_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // Listen for localStorage changes from other tabs/pages
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'hideDisabledStations') {
        setHideDisabledStations(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fetch stations with session details
  const { data: stations, isLoading: stationsLoading } = useQuery(
    ['supervisor-stations'],
    () => stationManagementService.getStations(),
    {
      keepPreviousData: true,
      staleTime: QUERY_REFETCH_NORMAL,
      refetchOnMount: 'always',
    }
  );

  // WebSocket for real-time station updates
  useEffect(() => {
    wsService.connect();

    const handleUpdate = () => {
      queryClient.invalidateQueries(['supervisor-stations']);
    };

    wsService.on('station-updated', handleUpdate);
    wsService.on('production-session-updated', handleUpdate);
    wsService.on('production-session-created', handleUpdate);
    wsService.on('worker-clocked-in', handleUpdate);
    wsService.on('worker-clocked-out', handleUpdate);
    wsService.on('box-packed', handleUpdate);
    wsService.on('box-cancelled', handleUpdate);
    wsService.on('box-deleted', handleUpdate);
    wsService.on('pending-label-voided', handleUpdate);

    return () => {
      wsService.off('station-updated', handleUpdate);
      wsService.off('production-session-updated', handleUpdate);
      wsService.off('production-session-created', handleUpdate);
      wsService.off('worker-clocked-in', handleUpdate);
      wsService.off('worker-clocked-out', handleUpdate);
      wsService.off('box-packed', handleUpdate);
      wsService.off('box-cancelled', handleUpdate);
      wsService.off('box-deleted', handleUpdate);
      wsService.off('pending-label-voided', handleUpdate);
    };
  }, [queryClient]);

  const handleViewDetails = (station: StationWithDetails) => {
    if (station.session_number) {
      setSelectedStation(station);
      setIsSessionDetailsModalOpen(true);
    }
  };

  const handleCloseDetails = () => {
    setIsSessionDetailsModalOpen(false);
    setSelectedStation(null);
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 xl:mb-3">
        <h2 className="text-base xl:text-lg font-semibold text-gray-900 dark:text-white">
          {t('manager.stationOverview')}
        </h2>
        <Link
          to="/station-management"
          className="text-sm xl:text-base text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {t('common.viewAll')}
        </Link>
      </div>

      {/* Body */}
      {stationsLoading && !stations ? (
        <div className="flex items-center justify-center py-4 flex-1">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </div>
      ) : !stations || stations.length === 0 ? (
        <p className="text-sm xl:text-base text-gray-400 dark:text-gray-500 text-center py-4 italic flex-1 flex items-center justify-center">
          {t('supervisor.noStationsFound')}
        </p>
      ) : (() => {
        const enabledStationsList = hideDisabledStations
          ? stations.filter((s: StationWithDetails) => s.enabled !== false)
          : stations;
        // Assigned stations with sessions (have a real station_id)
        const activeStations = enabledStationsList.filter((s: StationWithDetails) =>
          s.station_id && (s.session_number || s.station_status === 'active' || s.station_status === 'received' || s.station_status === 'paused')
        );
        // Unassigned sessions (no station_id but have a session)
        const unassignedSessions = enabledStationsList.filter((s: StationWithDetails) =>
          !s.station_id && s.session_number
        );
        const idleStations = enabledStationsList.filter((s: StationWithDetails) =>
          s.station_id && !s.session_number && s.station_status !== 'active' && s.station_status !== 'received' && s.station_status !== 'paused'
        );

        return (
          <>
            {/* Active stations — card grid */}
            {activeStations.length > 0 && (
              <div className={activeStations.length >= 3 ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 xl:gap-3" : activeStations.length === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-2 xl:gap-3" : "space-y-2 xl:space-y-3"}>
                {activeStations.map((station: StationWithDetails) => {
                  const statusBadgeClass = getStatusBadgeClass(station.station_status);

                  const targetBoxes = Number(station.total_boxes) || 0;
                  const producedBoxes = Number(station.boxes_produced) || 0;
                  const progress = targetBoxes > 0 ? Math.min((producedBoxes / targetBoxes) * 100, 100) : 0;
                  const progressLabel = targetBoxes > 0 ? `${producedBoxes}/${targetBoxes}` : '';

                  const barColor = ['active', 'in_progress'].includes(station.station_status)
                    ? 'bg-green-500'
                    : station.station_status === 'paused'
                    ? 'bg-amber-500'
                    : 'bg-blue-500';

                  return (
                    <div
                      key={station.station_id}
                      onClick={() => handleViewDetails(station)}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 xl:p-4 cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                    >
                      {/* Sub-session number + start time + status badge */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs xl:text-sm text-gray-400 dark:text-gray-500">
                          {(station.sub_session_number || station.session_number) || ''}
                          {station.started_at && (
                            <span className="ml-2 text-gray-300 dark:text-gray-600">
                              {new Date(station.started_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                              <span className="mx-1">·</span>
                              {new Date(station.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {station.has_pending_onhold && (
                            <span className="px-1.5 py-0.5 text-[10px] xl:text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              {t('supervisor.materialOnHold')}
                            </span>
                          )}
                          <span className={`px-1.5 xl:px-2 py-0.5 text-xs xl:text-sm font-medium rounded-full ${statusBadgeClass}`}>
                            {t(`status.${station.station_status}`) || station.station_status}
                          </span>
                        </div>
                      </div>
                      {/* Station name */}
                      <div className="mb-1 xl:mb-2">
                        <span className="text-sm xl:text-base font-medium text-gray-900 dark:text-white">
                          {station.station_name}
                        </span>
                      </div>

                      {/* Finished product: SKU, English name, Chinese name, unit weight + elapsed time */}
                      {(station.product_name || station.product_sku) && (
                        <div className="flex items-center justify-between mb-1 xl:mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {station.product_sku && (
                              <span className="text-xs xl:text-sm font-medium text-indigo-600 dark:text-indigo-400 shrink-0">
                                {station.product_sku}
                              </span>
                            )}
                            <span className="text-base xl:text-lg font-semibold text-gray-900 dark:text-white truncate">
                              {station.product_name}
                            </span>
                            {station.product_chinese_name && (
                              <span className="text-sm xl:text-base text-gray-400 dark:text-gray-500 shrink-0">
                                {station.product_chinese_name}
                              </span>
                            )}
                            {station.unit_weight && Number(station.unit_weight) > 0 && (
                              <span className="px-1.5 py-0.5 text-xs xl:text-sm font-medium rounded bg-blue-900/30 text-blue-400 shrink-0">
                                {Number(station.unit_weight).toFixed(2)}lb
                              </span>
                            )}
                          </div>
                          {station.started_at && (() => {
                            const mins = Math.floor((Date.now() - new Date(station.started_at).getTime()) / 60000);
                            if (mins < 1) return null;
                            const h = Math.floor(mins / 60);
                            const m = mins % 60;
                            return (
                              <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 ml-2">
                                {h > 0 ? `${h}h ${m}m` : `${m}m`}
                              </span>
                            );
                          })()}
                        </div>
                      )}

                      {/* Raw material */}
                      {station.raw_material_item_number && (
                        <div className="flex items-center gap-2 text-xs xl:text-sm text-gray-500 dark:text-gray-400 mb-1.5 xl:mb-2">
                          <span className="font-medium text-orange-600 dark:text-orange-400">{station.raw_material_item_number}</span>
                          <span className="truncate">{station.raw_material_name}</span>
                          {station.initial_material_weight && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">|</span>
                              <span>{Number(station.initial_material_weight).toFixed(2)} {t('supervisor.lbs')}</span>
                            </>
                          )}
                        </div>
                      )}

                      {/* Operator + Packer row */}
                      <div className="grid grid-cols-2 gap-1.5 text-sm xl:text-base text-gray-500 dark:text-gray-400 mb-1.5 xl:mb-2">
                        <div className="flex items-center gap-1.5">
                          {!station.operator_is_active && station.current_operator_name ? (
                            <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 rounded px-1.5 py-0.5">
                              <Scissors className="w-4 h-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              <span className="truncate text-amber-600 dark:text-amber-400">{station.current_operator_name}</span>
                            </span>
                          ) : (
                            <>
                              <Scissors className="w-4 h-4 shrink-0 text-green-500 dark:text-green-400" />
                              <span className="truncate">{station.current_operator_name || station.assigned_operator_name || '—'}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {!station.packer_is_active && station.current_packer_name ? (
                            <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 rounded px-1.5 py-0.5">
                              <PackageCheck className="w-4 h-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              <span className="truncate text-amber-600 dark:text-amber-400">{station.current_packer_name}</span>
                            </span>
                          ) : (
                            <>
                              <PackageCheck className="w-4 h-4 shrink-0 text-blue-500 dark:text-blue-400" />
                              <span className="truncate">{station.current_packer_name || '—'}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Box progress bar */}
                      {targetBoxes > 0 && (
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 xl:h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                              className={`h-1.5 xl:h-2 rounded-full ${barColor} transition-all duration-300`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm xl:text-base font-semibold text-gray-500 dark:text-gray-400 shrink-0">
                            {progress.toFixed(2)}% · {progressLabel}
                          </span>
                        </div>
                      )}

                      {/* Queue count badge */}
                      {station.queue_count > 0 && (
                        <div className="mt-1">
                          <span className="px-1.5 py-0.5 text-[10px] xl:text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            {(t('supervisor.queueCount') || '{{count}} in queue').replace('{{count}}', String(station.queue_count))}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unassigned sessions — awaiting station assignment */}
            {unassignedSessions.length > 0 && (
              <div className={activeStations.length > 0 ? "mt-3" : ""}>
                <div className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2">
                  {t('supervisor.unassignedSessions')} ({unassignedSessions.length})
                </div>
                <div className={unassignedSessions.length >= 3 ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 xl:gap-3" : unassignedSessions.length === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-2 xl:gap-3" : "space-y-2 xl:space-y-3"}>
                  {unassignedSessions.map((station: StationWithDetails, idx: number) => {
                    const statusBadgeClass = getStatusBadgeClass(station.station_status);
                    return (
                      <div
                        key={`unassigned-${station.sub_session_number || station.session_number || idx}`}
                        onClick={() => handleViewDetails(station)}
                        className="border border-dashed border-amber-300/50 dark:border-amber-700/40 bg-transparent rounded-lg p-3 xl:p-4 cursor-pointer hover:border-amber-400 dark:hover:border-amber-600 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 transition-colors"
                      >
                        {/* Sub-session number + status badge */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs xl:text-sm text-gray-400 dark:text-gray-500">
                            {station.sub_session_number || station.session_number || ''}
                          </span>
                          <span className={`px-1.5 xl:px-2 py-0.5 text-xs xl:text-sm font-medium rounded-full ${statusBadgeClass}`}>
                            {t(`status.${station.station_status}`) || station.station_status}
                          </span>
                        </div>
                        {/* No station label + on-hold badge */}
                        <div className="mb-1 xl:mb-2 flex items-center gap-2">
                          <span className="text-sm xl:text-base font-medium text-amber-600 dark:text-amber-400">
                            {t('supervisor.noStationAssigned')}
                          </span>
                          {station.has_pending_onhold && (
                            <span className="px-1.5 py-0.5 text-[10px] xl:text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              {t('supervisor.materialOnHold')}
                            </span>
                          )}
                        </div>

                        {/* Product info */}
                        {(station.product_name || station.product_sku) && (
                          <div className="flex items-center gap-2 min-w-0 mb-1 xl:mb-2">
                            {station.product_sku && (
                              <span className="text-xs xl:text-sm font-medium text-indigo-600 dark:text-indigo-400 shrink-0">
                                {station.product_sku}
                              </span>
                            )}
                            <span className="text-base xl:text-lg font-semibold text-gray-900 dark:text-white truncate">
                              {station.product_name}
                            </span>
                            {station.product_chinese_name && (
                              <span className="text-sm xl:text-base text-gray-400 dark:text-gray-500 shrink-0">
                                {station.product_chinese_name}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Raw material */}
                        {station.raw_material_item_number && (
                          <div className="flex items-center gap-2 text-xs xl:text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-orange-600 dark:text-orange-400">{station.raw_material_item_number}</span>
                            <span className="truncate">{station.raw_material_name}</span>
                            {station.initial_material_weight && (
                              <>
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <span>{Number(station.initial_material_weight).toFixed(2)} {t('supervisor.lbs')}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Idle / Disabled stations — compact row */}
            {idleStations.length > 0 && (
              <div className={(activeStations.length > 0 || unassignedSessions.length > 0) ? "mt-3" : ""}>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  {t('supervisor.idleStations')} ({idleStations.length})
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                  {idleStations.map((station: StationWithDetails) => (
                    <button
                      key={station.station_id}
                      onClick={() => handleViewDetails(station)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                        station.enabled === false
                          ? 'opacity-40 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                          : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${station.enabled === false ? 'bg-gray-300' : 'bg-gray-400'}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{station.station_name}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {station.enabled === false ? t('supervisor.disabled') : t('supervisor.idle')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Session Details Modal */}
      {isSessionDetailsModalOpen && selectedStation && selectedStation.session_number && (
        <SessionDetailsModal
          session={{
            session_number: selectedStation.session_number || '',
            product_sku: selectedStation.product_sku || '',
            product_name: selectedStation.product_name || '',
            status: selectedStation.session_status || '',
            station_id: selectedStation.station_id,
            station_name: selectedStation.station_name,
            raw_material_item_number: selectedStation.raw_material_item_number || undefined,
            raw_material_name: selectedStation.raw_material_name || undefined,
            initial_material_weight: selectedStation.initial_material_weight || undefined,
            target_output_weight: selectedStation.target_output_weight || undefined,
            actual_output_weight: selectedStation.actual_output_weight || undefined,
            started_at: selectedStation.started_at,
          }}
          onClose={handleCloseDetails}
          initialTab={getDefaultSessionTab(user?.role)}
        />
      )}
    </>
  );
};

export default StationOverview;
